import { useInfiniteQuery, useQueryClient, useMutation } from "@tanstack/react-query"
import * as Sentry from "@sentry/react"
import postsApi, { POSTS_PER_PAGE } from "../../../services/postsApi"
import type { Post, PostsResponse } from "../models/posts"

interface PostsFilter {
  tags: number[]
  query: string
  type?: number
}

type PostsQuery = {
  pages: PostsResponse[]
  pageParams: number[]
}

type UserFromCache = { id: number; username: string; name: string; avatar: string | null }

export const usePostsInfinite = (filter: PostsFilter = { tags: [], query: "" }) => {
  return useInfiniteQuery({
    queryKey: ["posts", filter],
    queryFn: async ({ pageParam = 1 }) => {
      return postsApi.getPosts(pageParam, filter.tags, filter.query, filter.type)
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.posts.length < POSTS_PER_PAGE) return undefined
      if (allPages.reduce((sum, p) => sum + p.posts.length, 0) >= lastPage.max_posts) return undefined
      return allPages.length + 1
    },
    initialPageParam: 1,
    staleTime: 1000 * 60 * 2,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

function updatePostInCache(
  qc: ReturnType<typeof useQueryClient>,
  postId: number,
  updater: (post: Post) => Post,
) {
  qc.setQueriesData<PostsQuery>({ queryKey: ["posts"] }, (old) => {
    if (!old) return old
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        posts: page.posts.map((post) => (post.id === postId ? updater(post) : post)),
      })),
    }
  })
}

export const useLikePost = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (postId: number) => postsApi.likePost(postId),
    onMutate: async (postId) => {
      await qc.cancelQueries({ queryKey: ["posts"] })
      const snapshot = qc.getQueriesData<PostsQuery>({ queryKey: ["posts"] })

      const userData = qc.getQueryData<{ user: UserFromCache }>(["user"])
      const currentUser = userData?.user

      qc.setQueriesData<PostsQuery>({ queryKey: ["posts"] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            posts: page.posts.map((post) => {
              if (post.id !== postId) return post
              const wasLiked = post.user_like
              return {
                ...post,
                user_like: !wasLiked,
                likes: wasLiked
                  ? post.likes.filter((l) => l.user_id !== currentUser?.id)
                  : currentUser
                    ? [...post.likes, { user_id: currentUser.id, username: currentUser.username, name: currentUser.name, avatar: currentUser.avatar }]
                    : post.likes,
              }
            }),
          })),
        }
      })

      return { snapshot }
    },
    onError: (error, _postId, context) => {
      Sentry.captureException(error)
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          qc.setQueryData(key, data)
        }
      }
    },
  })
}

export const useDeletePost = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (postId: number) => postsApi.deletePost(postId),
    onMutate: async (postId) => {
      await qc.cancelQueries({ queryKey: ["posts"] })
      const snapshot = qc.getQueriesData<PostsQuery>({ queryKey: ["posts"] })

      qc.setQueriesData<PostsQuery>({ queryKey: ["posts"] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            posts: page.posts.filter((p) => p.id !== postId),
            max_posts: page.max_posts - 1,
          })),
        }
      })

      return { snapshot }
    },
    onError: (error, _postId, context) => {
      Sentry.captureException(error)
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          qc.setQueryData(key, data)
        }
      }
    },
  })
}

export const useUpdatePost = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ postId, content }: { postId: number; content: string }) =>
      postsApi.updatePost(postId, content),
    onSuccess: (updatedPost, { postId }) => {
      updatePostInCache(qc, postId, (post) => ({ ...post, ...updatedPost }))
    },
    onError: (error) => {
      Sentry.captureException(error)
    },
  })
}

export const useModeratePost = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (postId: number) => postsApi.moderatePost(postId),
    onSuccess: (updatedPost, postId) => {
      updatePostInCache(qc, postId, (post) => ({ ...post, ...updatedPost }))
    },
    onError: (error) => {
      Sentry.captureException(error)
    },
  })
}

export const useCreatePost = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (formData: FormData) => postsApi.createPost(formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posts"] })
    },
    onError: (error) => {
      Sentry.captureException(error)
    },
  })
}

export const useRefreshPostMessages = () => {
  const qc = useQueryClient()

  return async (postId: number) => {
    const messages = await postsApi.getPostMessages(postId)
    qc.setQueriesData<PostsQuery>({ queryKey: ["posts"] }, (old) => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          posts: page.posts.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  user_like: messages.user_like,
                  likes: messages.likes,
                  comments: messages.comments,
                  comments_length: messages.comments_length,
                }
              : post,
          ),
        })),
      }
    })
  }
}

export const useNewCommentMutation = () =>
  useMutation({
    mutationFn: ({
      postId,
      parentId,
      content,
    }: {
      postId: number
      parentId: number
      content: string
    }) => postsApi.newComment(postId, parentId, content),
    onError: (error) => {
      Sentry.captureException(error)
    },
  })

export const useUpdateCommentMutation = () =>
  useMutation({
    mutationFn: ({ commentId, content }: { commentId: number; content: string }) =>
      postsApi.updateComment(commentId, content),
    onError: (error) => {
      Sentry.captureException(error)
    },
  })

export const useDeleteCommentMutation = () =>
  useMutation({
    mutationFn: (commentId: number) => postsApi.deleteComment(commentId),
    onError: (error) => {
      Sentry.captureException(error)
    },
  })

export const useModerateCommentMutation = () =>
  useMutation({
    mutationFn: (commentId: number) => postsApi.moderateComment(commentId),
    onError: (error) => {
      Sentry.captureException(error)
    },
  })

